import { t } from "@lingui/macro";
import Router from "next/router";
import { useEffect } from "react";

type HasUnsavedChanges = boolean | (() => boolean);

const getHasUnsavedChanges = (condition: HasUnsavedChanges): boolean => {
  return typeof condition === "function" ? condition() : condition;
};

export function useWarnBeforeUnload(
  hasUnsavedChanges: HasUnsavedChanges,
  message?: string,
): void {
  const defaultMessage = t`You have unsaved changes. Are you sure you want to leave the page?`;
  const confirmMessage = message ?? defaultMessage;

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!getHasUnsavedChanges(hasUnsavedChanges)) return;

      event.preventDefault();
    };

    const handleRouteChange = () => {
      if (!getHasUnsavedChanges(hasUnsavedChanges)) return;

      if (!window.confirm(confirmMessage)) {
        Router.events.emit("routeChangeError");
        throw t`routeChange aborted: [unsaved changes]`;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    Router.events.on("routeChangeStart", handleRouteChange);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      Router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [hasUnsavedChanges, confirmMessage]);
}
